/**
 * One erase stroke. Hits are marked on the existing segment indexes.
 * The graph is compacted once, when the pointer goes up.
 */

export interface EraseStrokeState {
  /** 1 once that segment has been taken by this stroke. */
  hidden: Uint8Array;
  degree: Uint16Array;
  hits: number;
}

export interface EraseBatch {
  segments: number[];
  orphans: number[];
}

export function beginEraseStroke(
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
): EraseStrokeState {
  const degree = new Uint16Array(pointCount);
  for (const pair of segments) {
    if (!pair) continue;
    const [a, b] = pair;
    if (a >= 0 && a < pointCount) degree[a] += 1;
    if (b >= 0 && b < pointCount) degree[b] += 1;
  }
  return { hidden: new Uint8Array(segments.length), degree, hits: 0 };
}

/** Mark new brush hits. A point is an orphan only on the hit that drops its degree to zero. */
export function consumeEraseHits(
  state: EraseStrokeState,
  hits: readonly number[],
  segments: ReadonlyArray<readonly [number, number]>,
): EraseBatch {
  const fresh: number[] = [];
  const orphans: number[] = [];
  for (const index of hits) {
    if (index < 0 || index >= state.hidden.length || state.hidden[index]) continue;
    const pair = segments[index];
    if (!pair) continue;
    state.hidden[index] = 1;
    state.hits += 1;
    fresh.push(index);
    for (const end of pair) {
      if (end < 0 || end >= state.degree.length || state.degree[end] === 0) continue;
      state.degree[end] -= 1;
      if (state.degree[end] === 0) orphans.push(end);
    }
  }
  return { segments: fresh, orphans };
}

/**
 * Lines the eraser will take. A point inside the brush adds the lines that
 * touch it only while points are visible. Hidden points add nothing.
 */
export function eraseTargets(
  lineHits: readonly number[],
  pointHits: readonly number[],
  segments: ReadonlyArray<readonly [number, number]>,
  pointsVisible: boolean,
): number[] {
  const found: number[] = [];
  const seen = new Set<number>();
  const add = (index: number) => {
    if (!Number.isInteger(index) || index < 0 || index >= segments.length || seen.has(index)) return;
    seen.add(index);
    found.push(index);
  };
  for (const index of lineHits) add(index);
  if (!pointsVisible || pointHits.length === 0) return found;
  const points = new Set(pointHits);
  for (let i = 0; i < segments.length; i++) {
    const pair = segments[i];
    if (!pair) continue;
    if (points.has(pair[0]) || points.has(pair[1])) add(i);
  }
  return found;
}

export function removedSegmentIndexes(state: EraseStrokeState): number[] {
  const removed: number[] = [];
  for (let i = 0; i < state.hidden.length; i++) {
    if (state.hidden[i]) removed.push(i);
  }
  return removed;
}
